import React, { useEffect, useState } from 'react';
import { Link, useLocation as useRouterLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLocation as useLocationCtx } from '../context/LocationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  CheckSquare,
  Megaphone,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  Bell,
  CalendarDays,
  Plane,
  Inbox,
  ArrowLeftRight,
  CalendarRange,
  TrendingUp,
  CreditCard,
  Headset,
  Building2,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  ClipboardList,
  BookOpen,
  Download,
  DollarSign,
  Activity,
} from 'lucide-react';
import { Button } from './ui/button';
import { ProductTour } from './ProductTour';
import usePWAInstall from '../hooks/usePWAInstall';
import { EvaChat } from './EvaChat';
import TrialBanner from './TrialBanner';
import DemoBadge from './DemoBadge';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', translationKey: 'nav.dashboard', roles: ['owner', 'manager', 'staff'] },
  { path: '/staff', icon: Users, label: 'Staff', translationKey: 'nav.staff', roles: ['owner', 'manager'], tourId: 'staff' },
  { path: '/time-clock', icon: Clock, label: 'Time Clock', translationKey: 'nav.timeClock', roles: ['owner', 'manager', 'staff'], tourId: 'timeclock' },
  { path: '/schedule', icon: Calendar, label: 'Schedule', translationKey: 'nav.schedule', roles: ['owner', 'manager', 'staff'], tourId: 'schedule' },
  { path: '/availability', icon: CalendarDays, label: 'My Availability', roles: ['staff', 'manager'] },
  { path: '/absences', icon: CalendarRange, label: 'Absences', roles: ['__hidden__'] },
  { path: '/shift-swaps', icon: ArrowLeftRight, label: 'Shift Swaps', translationKey: 'nav.shiftSwaps', roles: ['owner', 'manager', 'staff'], tourId: 'shiftswaps' },
  { path: '/tasks', icon: CheckSquare, label: 'Tasks', translationKey: 'nav.tasks', roles: ['owner', 'manager', 'staff'] },
  { path: '/announcements', icon: Megaphone, label: 'Announcements', translationKey: 'nav.announcements', roles: ['owner', 'manager', 'staff'] },
  { path: '/notifications', icon: Bell, label: 'Notifications', translationKey: 'nav.notifications', roles: ['owner', 'manager', 'staff'] },
  { path: '/documents', icon: FileText, label: 'Documents', translationKey: 'nav.documents', roles: ['owner', 'manager', 'staff'], tourId: 'documents' },
  { path: '/executive-insights', icon: TrendingUp, label: 'Executive Insights', translationKey: 'nav.insights', roles: ['owner', 'manager'], tourId: 'insights' },
  { path: '/timesheets', icon: ClipboardList, label: 'Timesheets', translationKey: 'nav.timesheets', roles: ['owner', 'manager', 'staff'], tourId: 'timesheets' },
  { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['owner', 'manager'], tourId: 'reports' },
  { path: '/audit-log', icon: Activity, label: 'Eva Audit Log', roles: ['owner', 'manager'] },
  { path: '/admin/holidays', icon: CalendarDays, label: 'Holidays', roles: ['owner', 'manager'] },
  { path: '/leave', icon: Plane, label: 'Leave', roles: ['owner', 'manager', 'staff'] },
  { path: '/shift-replacements', icon: Inbox, label: 'Replacements', roles: ['owner', 'manager', 'staff'], badgeKey: 'replacement_unread' },
  { path: '/payslips', icon: DollarSign, label: 'Payslips', roles: ['owner', 'manager', 'staff'] },
  { path: '/account-security', icon: ShieldCheck, label: 'Account Security', roles: ['owner', 'manager', 'super_admin'] },
  { path: '/support', icon: Headset, label: 'Support', roles: ['owner', 'manager', 'staff'] },
  { path: '/billing', icon: CreditCard, label: 'Billing', roles: ['owner'] },
  { path: '/settings', icon: Settings, label: 'Settings', translationKey: 'nav.settings', roles: ['owner', 'manager'] },
];

const mobileNavItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Home' },
  { path: '/time-clock', icon: Clock, label: 'Clock' },
  { path: '/schedule', icon: Calendar, label: 'Schedule' },
  { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
  { path: '/notifications', icon: Bell, label: 'Alerts' },
];

export const Layout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useRouterLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [badges, setBadges] = useState({});

  // Lightweight badge poller — single endpoint, 60s interval. Lives in
  // Layout so it's effectively a single instance per session.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { shiftReplacementAPI } = await import('../lib/api');
        const r = await shiftReplacementAPI.unreadCount();
        if (!cancelled) setBadges((b) => ({ ...b, replacement_unread: r.data?.unread || 0 }));
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { installPrompt, isInstalled, install } = usePWAInstall();
  const {
    locations: allLocations,
    selectedLocation,
    selectedLocationId,
    setSelectedLocation,
    canSwitchLocation,
    canSelectAll,
  } = useLocationCtx();

  const handleLogout = () => {
    // Clear this user's Eva chat from localStorage before logout
    if (user?.id || user?._id) {
      localStorage.removeItem(`eva_chat_${user?.id || user?._id}`);
    }
    logout();
    navigate('/');
  };

  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(user?.role) || user?.role === 'super_admin'
  );

  return (
    <div className="min-h-screen bg-slate-50 flex overflow-hidden">
      <ProductTour />
      {/* Desktop Sidebar */}
      <aside
        className="sidebar hidden md:flex flex-col flex-shrink-0"
        style={{
          width: collapsed ? 70 : 256,
          minWidth: collapsed ? 70 : 256,
          transition: 'width 250ms ease, min-width 250ms ease',
        }}
      >
        {/* Logo + collapse toggle */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between" style={{ minHeight: 65 }}>
          <Link to="/dashboard" className="flex items-center gap-2 overflow-hidden">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span
              className="font-bold text-lg text-slate-900"
              style={{
                fontFamily: 'Manrope',
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : 'auto',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'opacity 200ms ease, width 250ms ease',
              }}
            >
              ReStaff
            </span>
          </Link>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-collapse-btn"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const badge = item.badgeKey ? badges[item.badgeKey] : 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                style={{ justifyContent: collapsed ? 'center' : undefined, padding: collapsed ? '0.75rem' : undefined, position: 'relative' }}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                {...(item.tourId ? { 'data-tour': item.tourId } : {})}
              >
                <div style={{ position: 'relative' }} className="flex-shrink-0">
                  <Icon size={20} />
                  {badge > 0 && collapsed && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-[3px] rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center"
                      data-testid={`sidebar-badge-${item.badgeKey}`}
                    >
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                <span
                  style={{
                    opacity: collapsed ? 0 : 1,
                    width: collapsed ? 0 : 'auto',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 200ms ease, width 250ms ease',
                  }}
                >
                  {item.translationKey ? t(item.translationKey) : item.label}
                </span>
                {badge > 0 && !collapsed && (
                  <span
                    className="ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
                    data-testid={`sidebar-badge-${item.badgeKey}`}
                  >
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </Link>
            );
          })}

          {isSuperAdmin && (
            <Link
              to="/super-admin"
              title={collapsed ? 'Super Admin' : undefined}
              className={`sidebar-link ${location.pathname === '/super-admin' ? 'active' : ''}`}
              style={{ justifyContent: collapsed ? 'center' : undefined, padding: collapsed ? '0.75rem' : undefined }}
              data-testid="nav-super-admin"
            >
              <Shield size={20} className="flex-shrink-0" />
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  width: collapsed ? 0 : 'auto',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 200ms ease, width 250ms ease',
                }}
              >
                Super Admin
              </span>
            </Link>
          )}

          {(user?.role === 'owner' || user?.role === 'manager') && (
            <Link
              to="/feature-request"
              title={collapsed ? 'Need New Features?' : undefined}
              className={`sidebar-link ${location.pathname === '/feature-request' ? 'active' : ''}`}
              style={{ justifyContent: collapsed ? 'center' : undefined, padding: collapsed ? '0.75rem' : undefined }}
              data-testid="nav-need-new-features"
            >
              <Lightbulb size={20} className="flex-shrink-0" />
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  width: collapsed ? 0 : 'auto',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 200ms ease, width 250ms ease',
                }}
              >
                Need New Features?
              </span>
            </Link>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200 flex-shrink-0">
          {/* User info */}
          <div
            className="flex items-center gap-3 mb-3 px-1 overflow-hidden"
            style={{ justifyContent: collapsed ? 'center' : undefined }}
          >
            <div
              className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0"
              title={collapsed ? user?.name : undefined}
            >
              <span className="text-slate-600 font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div
              className="flex-1 min-w-0"
              style={{
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : 'auto',
                overflow: 'hidden',
                transition: 'opacity 200ms ease, width 250ms ease',
              }}
            >
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>

          {/* Help Center */}
          <Link
            to="/help"
            title={collapsed ? 'Help Center' : undefined}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
            style={{ justifyContent: collapsed ? 'center' : undefined }}
            data-testid="help-center-link"
          >
            <BookOpen size={18} className="flex-shrink-0" />
            <span
              style={{
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : 'auto',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'opacity 200ms ease, width 250ms ease',
              }}
            >
              Help Center
            </span>
          </Link>

          {/* PWA Install Button */}
          {installPrompt && !isInstalled && (
            <button
              onClick={install}
              title={collapsed ? 'Install ReStaff' : undefined}
              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors font-medium"
              style={{ justifyContent: collapsed ? 'center' : undefined }}
              data-testid="pwa-install-btn"
            >
              <Download size={18} className="flex-shrink-0" />
              <span
                style={{
                  opacity: collapsed ? 0 : 1,
                  width: collapsed ? 0 : 'auto',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 200ms ease, width 250ms ease',
                }}
              >
                Install ReStaff
              </span>
            </button>
          )}

          {/* Help Center */}
          <Link
            to="/help"
            title={collapsed ? t('nav.helpCenter') : undefined}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
            style={{ justifyContent: collapsed ? 'center' : undefined }}
            data-testid="help-center-btn"
          >
            <BookOpen size={18} className="flex-shrink-0" />
            <span
              style={{
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : 'auto',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'opacity 200ms ease, width 250ms ease',
              }}
            >
              {t('nav.helpCenter')}
            </span>
          </Link>

          {/* Logout */}
          <button
            onClick={handleLogout}
            title={collapsed ? 'Logout' : undefined}
            className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            style={{ justifyContent: collapsed ? 'center' : undefined }}
            data-testid="logout-btn"
          >
            <LogOut size={18} className="flex-shrink-0" />
            <span
              style={{
                opacity: collapsed ? 0 : 1,
                width: collapsed ? 0 : 'auto',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'opacity 200ms ease, width 250ms ease',
              }}
            >
              {t('nav.logout')}
            </span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-50">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">RS</span>
          </div>
          <span className="font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            ReStaff
          </span>
          <DemoBadge compact />
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-slate-600"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Sidebar Backdrop */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-[55]"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-sidebar-backdrop"
        />
      )}

      {/* Mobile Slide-in Sidebar (left side) */}
      <div
        className="md:hidden fixed left-0 top-0 bottom-0 w-72 bg-white z-[60] flex flex-col shadow-xl"
        style={{
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
        }}
        data-testid="mobile-sidebar-panel"
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-4 border-b border-slate-200 flex-shrink-0" style={{ minHeight: 56 }}>
          <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span className="font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>ReStaff</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-slate-500 hover:text-slate-700 rounded-md"
            data-testid="mobile-sidebar-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={20} className="flex-shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
          {isSuperAdmin && (
            <Link
              to="/super-admin"
              className={`sidebar-link ${location.pathname === '/super-admin' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Shield size={20} className="flex-shrink-0" />
              <span>Super Admin</span>
            </Link>
          )}
          {(user?.role === 'owner' || user?.role === 'manager') && (
            <Link
              to="/feature-request"
              className={`sidebar-link ${location.pathname === '/feature-request' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-need-new-features"
            >
              <Lightbulb size={20} className="flex-shrink-0" />
              <span>Need New Features?</span>
            </Link>
          )}
        </nav>

        {/* User info + actions — always visible at bottom */}
        <div className="p-3 border-t border-slate-200 flex-shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-slate-600 font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          {installPrompt && !isInstalled && (
            <button
              className="w-full flex items-center gap-2 px-2 py-2.5 rounded-md text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors mb-1"
              onClick={() => { install(); setMobileMenuOpen(false); }}
              data-testid="mobile-pwa-install-btn"
            >
              <Download size={18} className="flex-shrink-0" />
              Install ReStaff
            </button>
          )}
          <Link
            to="/help"
            className="w-full flex items-center gap-2 px-2 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors mb-1"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-nav-help-center"
          >
            <BookOpen size={18} className="flex-shrink-0" />
            Help Center
          </Link>
          <button
            className="w-full flex items-center gap-2 px-2 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
            onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
            data-testid="mobile-logout-btn"
          >
            <LogOut size={18} className="flex-shrink-0" />
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-link ${isActive ? 'active' : ''}`}
            >
              <Icon size={22} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Main Content */}
      <main className="main-content pt-14 md:pt-0 flex-1 min-w-0">
        <TrialBanner />
        {/* Location Switcher Bar */}
        {canSwitchLocation && allLocations.length > 0 && (
          <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="location-switcher-bar">
            <span className="text-xs font-medium text-slate-500 shrink-0">Viewing:</span>
            <Select value={selectedLocationId || ''} onValueChange={setSelectedLocation}>              <SelectTrigger className="w-auto min-w-[180px] max-w-[280px] h-9 text-sm border-slate-200" data-testid="location-switcher-select">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {canSelectAll && (
                  <SelectItem value="all" data-testid="location-option-all">All Locations</SelectItem>
                )}
                {allLocations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id} data-testid={`location-option-${loc.id}`}>
                    {loc.location_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedLocation && selectedLocation.id !== 'all' && (
              <span className="flex items-center gap-2 min-w-0" data-testid="location-name-display">
                {selectedLocation.logo_url ? (
                  <img
                    src={selectedLocation.logo_url}
                    alt={selectedLocation.location_name}
                    width={28}
                    height={28}
                    loading="lazy"
                    className="rounded-md object-contain flex-shrink-0 border border-slate-200 bg-white"
                    style={{ width: 28, height: 28 }}
                    onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <span
                  className="rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0"
                  style={{ width: 28, height: 28, display: selectedLocation.logo_url ? 'none' : 'flex' }}
                  data-testid="location-logo-placeholder"
                >
                  <Building2 size={14} className="text-slate-400" />
                </span>
                <span className="text-sm font-semibold text-slate-800 truncate max-w-[140px] sm:max-w-[220px] hidden sm:inline">
                  {selectedLocation.location_name}
                </span>
              </span>
            )}
            {selectedLocation && selectedLocation.id === 'all' && (
              <span className="text-sm font-semibold text-slate-800 hidden sm:inline" data-testid="location-name-display">
                All Locations
              </span>
            )}
            <div className="ml-auto"><DemoBadge /></div>
          </div>
        )}
        {/* Demo badge — fallback placement when location switcher bar is hidden (e.g. staff demo) */}
        {!(canSwitchLocation && allLocations.length > 0) && (
          <div className="hidden md:flex justify-end px-4 pt-2">
            <DemoBadge />
          </div>
        )}
        {children}
      </main>
      {/* Eva AI assistant widget — isolated, owner/manager only */}
      <EvaChat />
    </div>
  );
};

export default Layout;
